/**
 * Application Feature Catalog (@aiga/app-config/permissions)
 *
 * Canonical list of "features" the application exposes — the modules,
 * pages, and major workflows an admin can grant a role access to.
 * This catalog is the alignment point between the role-management UI
 * and what the app actually ships today.
 *
 * Persistence strategy: a feature is stored as a row in the existing
 * `permissions` table with `perm_name = FEATURE_PERM_PREFIX + key`.
 * The role↔feature link is the existing `role_permissions` join. This
 * avoids a new table while still letting `hasPermission('feature:X')`
 * gate UI/routes.
 *
 * To add a new feature: append an entry here. The Feature Access UI
 * picks it up automatically; the matching `permissions` row is
 * created lazily the first time an admin grants it to a role.
 *
 * Moved from the web app's config/feature-catalog.ts by CONFIG CF-4 so the
 * API names its keys through `featurePerm('<key>')` (a key that is not in
 * this catalog fails tsc) instead of spelling 'feature:…' strings.
 */
export const FEATURE_PERM_PREFIX = 'feature:';
export const FEATURE_CATEGORIES = {
    core: 'Core Workspace',
    transactions: 'Deals & Transactions',
    intelligence: 'Comp Intelligence',
    operations: 'Operations & Review',
    data: 'Data Management',
    listings: 'Listings',
    property_management: 'Property Management',
    admin: 'Administration',
};
export const FEATURE_MODULES = {
    dashboard: 'Dashboard',
    properties: 'Properties',
    deals: 'Deals',
    comp_intelligence: 'Comp Intelligence',
    comp_requests: 'Comp Requests',
    research_queue: 'Research queue',
    comp_reports: 'Comp Reports',
    reviews: 'Reviews',
    verification: 'Verification',
    data_ingestion: 'Data Ingestion',
    ops_dashboard: 'Ops Dashboard',
    documents: 'Documents',
    listings: 'Listings',
    property_management: 'Property management',
    users: 'Users',
    directory: 'Directory',
    settings: 'Settings',
    records: 'Records (all modules)',
};
/** The module with no view switch of its own (see {@link FeatureModule}). */
export const CROSS_MODULE_FEATURE_MODULE = 'records';
const CATALOG = [
    // Core workspace
    {
        key: 'dashboard',
        label: 'Dashboard: see the page',
        description: 'Open the Dashboard. Which cards it shows is set per role on Settings → Widget Permissions.',
        category: 'core',
        module: 'dashboard',
        kind: 'view',
        defaultTier: 'all',
        path: '/dashboard',
    },
    {
        key: 'properties',
        label: 'Properties: see the module',
        description: 'Open Properties and read property records.',
        category: 'core',
        module: 'properties',
        kind: 'view',
        defaultTier: 'all',
        path: '/properties',
    },
    // Property action switches: create / edit default to today's rule, useCanEditProperty / the API's
    // canEditProperty (Manager tier + Research). Suggest is the review path for the roles without edit.
    {
        key: 'properties_create',
        label: 'Properties: create',
        description: 'Add a new property.',
        category: 'core',
        module: 'properties',
        kind: 'action',
        defaultTier: 'manager+research',
        path: '/properties/new',
    },
    {
        key: 'properties_edit',
        label: 'Properties: edit',
        description: 'Change property data, photos and files directly. Without it, a user with "suggest edits" suggests a change for review instead.',
        category: 'core',
        module: 'properties',
        kind: 'action',
        defaultTier: 'manager+research',
    },
    {
        // The API checks it on a suggestion create whose target is a property (record-action-urls.ts);
        // transaction suggestions take comp_transactions_suggest. The web shows the suggest controls to holders
        // without properties_edit (useCanSuggestPropertyEdit).
        key: 'properties_suggest',
        label: 'Properties: suggest edits',
        description: 'Suggest a change to property data for review, without changing it directly.',
        category: 'core',
        module: 'properties',
        kind: 'action',
        defaultTier: 'any',
    },
    // Deals & transactions
    {
        key: 'deals',
        label: 'Deals: see the module',
        description: 'Open Deals: the deal pipeline and its deal records.',
        category: 'transactions',
        module: 'deals',
        kind: 'view',
        defaultTier: 'any',
        path: '/deals',
    },
    // 'broker_portal' was removed 2026-06-12 — the dashboard now renders
    // a Broker-tier layout when role.name === 'Broker', so a standalone
    // /broker URL + feature gate are no longer needed. The data layer
    // (property_assignments, property_submissions) is reached via
    // dashboard cards instead.
    // Comp intelligence
    //
    // Defaults reflect the tier-gating the app already runs (sidebar
    // tiers + in-page redirects). Once the seeder has materialised
    // these defaults into the permissions table, runtime gating reads
    // straight from the matrix — so admins editing this matrix really
    // does change who can see what.
    {
        key: 'comp_intelligence',
        label: 'Comp Intelligence: see the module',
        description: 'Open Comp Intelligence: search comps and read sale and lease transactions.',
        category: 'intelligence',
        module: 'comp_intelligence',
        kind: 'view',
        defaultTier: 'any',
        path: '/comps',
    },
    {
        key: 'comp_requests',
        label: 'Comp Requests: see the module',
        description: 'Open My Requests: submit comp requests to the research team and track them.',
        category: 'intelligence',
        module: 'comp_requests',
        kind: 'view',
        defaultTier: 'any',
        path: '/comps/requests',
    },
    {
        key: 'comp_research_queue',
        label: 'Research queue: see the queue',
        description: 'See the research team\'s inboxes: incoming comp requests and Broker Docs & Notes.',
        category: 'intelligence',
        module: 'research_queue',
        kind: 'view',
        defaultTier: 'manager+',
        path: '/comps/queue',
    },
    {
        // Grants eligibility to be assigned a comp request from /comps/queue.
        // Default tier 'research' (F3, 2026-07-22 Matt call): only the
        // Research role is assignable out of the box. The tier must stay
        // narrow — the Feature Access tab auto-seeds any role covered by
        // the tier on every visit, so a wider tier would silently re-grant
        // managers/admins after a revoke. Admins can still extend
        // eligibility to other roles via the matrix.
        key: 'comp_can_be_assigned_requests',
        label: 'Research queue: can be assigned requests',
        description: 'Be in the assignee list, so an incoming comp request can be assigned to you.',
        category: 'intelligence',
        module: 'research_queue',
        kind: 'action',
        defaultTier: 'manager+',
    },
    {
        // BF-MR (2026-06-25, Matt call): the reports page is now role-aware —
        // research/manager+ tier sees every report ("All Reports") so they
        // can edit on a broker's behalf; everyone else sees only their own
        // ("My Reports"). Tier dropped to 'any' so brokers can reach their
        // own list; cross-user visibility is gated inside the page (and on
        // the backend) by role rather than by the feature flag.
        key: 'comp_reports',
        label: 'Comp Reports: see the module',
        description: 'Open Comp Reports: your own comp reports and the ones shared with you.',
        category: 'intelligence',
        module: 'comp_reports',
        kind: 'view',
        defaultTier: 'any',
        path: '/comps/reports',
    },
    // Comp report action switches. Defaults are today's rules: anyone who reaches Comp Intelligence
    // starts a report; the creator, its deal brokers and edit-share recipients edit it, and the
    // Manager tier + Research edit every report (useReportPermissions canMarkFinished) and see every
    // report on All Reports (AllReportsPage seesAllReports).
    {
        key: 'comp_reports_create',
        label: 'Comp Reports: create',
        description: 'Start a new comp report.',
        category: 'intelligence',
        module: 'comp_reports',
        kind: 'action',
        defaultTier: 'any',
    },
    {
        key: 'comp_reports_edit',
        label: 'Comp Reports: edit every report',
        description: 'Edit any comp report, not only the ones you created, are a broker on, or were shared to edit.',
        category: 'intelligence',
        module: 'comp_reports',
        kind: 'action',
        defaultTier: 'manager+research',
    },
    {
        key: 'comp_reports_view_all',
        label: 'Comp Reports: see every report',
        description: 'See every comp report on All Reports, not only your own and the ones shared with you.',
        category: 'intelligence',
        module: 'comp_reports',
        kind: 'action',
        defaultTier: 'manager+research',
        path: '/comps/reports',
    },
    {
        // F-RR followup (2026-06-18): brokers see comp intelligence but
        // can't author transactions — that's a research / manager action.
        // Default tier manager+ so admins can extend via /settings/permissions
        // if a particular broker should be allowed to seed transactions
        // directly without the broker-submission inbox round trip.
        key: 'comp_transactions_create',
        label: 'Comp Intelligence: create transactions',
        description: 'Add a sale or lease transaction directly, without sending it through broker-submission review.',
        category: 'intelligence',
        module: 'comp_intelligence',
        kind: 'action',
        defaultTier: 'manager+',
    },
    {
        // properties_suggest's twin for a sale or lease target (record-action-urls.ts). The web shows the
        // suggest controls to holders outside the research tier, who edit transactions directly
        // (useCanSuggestTransactionEdit).
        key: 'comp_transactions_suggest',
        label: 'Comp Intelligence: suggest transaction edits',
        description: 'Suggest a change to a sale or lease transaction for review, without changing it directly.',
        category: 'intelligence',
        module: 'comp_intelligence',
        kind: 'action',
        defaultTier: 'any',
    },
    // Operations & review
    {
        key: 'reviews',
        label: 'Reviews: see the queues',
        description: 'Open the review queues: ingested data awaiting approval, and brokers\' suggested changes and property submissions.',
        category: 'operations',
        module: 'reviews',
        kind: 'view',
        defaultTier: 'manager+',
        path: '/reviews',
    },
    // 'reports_view_own', 'reports_create' and 'reports_view_all' were removed 2026-10-28: they gated an
    // operational reports module that never shipped (the /reports "Coming soon" page went with them).
    // Comp reports have their own comp_reports_* keys above. Their role_permissions rows are left in
    // place: nothing reads them, and deleting them could flip a role from matrix- to tier-governed.
    {
        key: 'ops_dashboard',
        label: 'Ops Dashboard: see the page',
        description: 'Open the Ops Dashboard: the health of pipelines and jobs.',
        category: 'operations',
        module: 'ops_dashboard',
        kind: 'view',
        defaultTier: 'manager+',
        path: '/ops',
    },
    {
        key: 'verification',
        label: 'Verification: see the module',
        description: 'Open Verification: check ingested records against their source documents.',
        category: 'operations',
        module: 'verification',
        kind: 'view',
        defaultTier: 'manager+',
        path: '/verification',
    },
    {
        key: 'documents',
        label: 'Documents: see the library',
        description: 'Open the Documents library of source uploads. The page is not in the menu yet.',
        category: 'operations',
        module: 'documents',
        kind: 'view',
        defaultTier: 'admin+',
        path: '/documents',
    },
    {
        // H-03 (2026-07-20): expanded source view inside the per-field
        // history popover — file-level attribution (document name, page,
        // link) and trust ranks. Matt's lean: research needs to judge
        // whether a source can be trusted; brokers don't. Brokers are
        // additionally excluded in-component via useIsBroker() regardless
        // of this grant; the tier here covers the pre-seed fallback for
        // everyone else (Research included via the 'any' tier).
        key: 'history_source_detail',
        label: 'Records: see source detail in field history',
        description: 'See the document, page, link and trust rank behind a value in the field-history popover. Brokers never see it.',
        category: 'operations',
        module: 'records',
        kind: 'action',
        defaultTier: 'any',
    },
    // Data management
    {
        key: 'data_ingestion',
        label: 'Data Ingestion: see the module',
        description: 'Open Data Ingestion: upload files or scrape sources to bring in new data.',
        category: 'data',
        module: 'data_ingestion',
        kind: 'view',
        defaultTier: 'manager+',
        path: '/ingestion',
    },
    {
        key: 'ingestion_rollback',
        label: 'Data Ingestion: roll back a job',
        description: 'Permanently delete every record one ingestion job created, from the job\'s detail page.',
        category: 'data',
        module: 'data_ingestion',
        kind: 'action',
        defaultTier: 'admin+',
        path: '/ingestion/jobs/:id',
    },
    // D2-S (Noah 2026-10-06): archive is the everyday way to remove a record;
    // permanent delete is Admin / Super Admin only. Migration
    // 20261006213105_d2s_archive_columns.sql writes the same grants.
    {
        key: 'can_archive_data',
        label: 'Records: archive and restore',
        description: 'Archive a property, transaction, parcel or comp report instead of deleting it, see archived records and restore them.',
        category: 'admin',
        module: 'records',
        kind: 'action',
        defaultTier: 'manager+research',
    },
    {
        key: 'can_delete_data',
        label: 'Records: permanently delete',
        description: 'Permanently delete a property (with its spaces and linked transactions), a transaction, a parcel or a comp report. Cannot be undone; history, parcels and snapshots are kept.',
        category: 'admin',
        module: 'records',
        kind: 'action',
        defaultTier: 'admin+',
    },
    // Listings (Phase 2). The hand-written Listings API checks these keys
    // (L1-1); the web module is shown for listings_work or an admin (L1-3,
    // components/listings/useListingsAccess).
    {
        key: 'listings',
        label: 'Listings: see the module',
        description: 'Open Listings and read listing agreements, their units and tasks, and the broker activity feed. Without assistant work, only the agreements you are on.',
        category: 'listings',
        module: 'listings',
        kind: 'view',
        defaultTier: 'listings',
        path: '/listings',
    },
    {
        key: 'listings_work',
        label: 'Listings: assistant work',
        description: 'Create listing agreements, add units, and edit, reassign and complete listing tasks.',
        category: 'listings',
        module: 'listings',
        kind: 'action',
        defaultTier: 'listings_work',
    },
    {
        key: 'listings_templates',
        label: 'Listings: edit task templates',
        description: 'Change the Sale, Lease and optional listing task templates and their steps.',
        category: 'listings',
        module: 'listings',
        kind: 'action',
        defaultTier: 'manager+',
    },
    // CONFIG CF-10: the edit key of the `listings.compliance` settings group (@aiga/app-config/settings),
    // shown on Settings → App settings. No permission row exists until CF-11's migration seeds it to
    // Super Admin and Admin; until then only the admin bypass passes (the rule listing_settings has today).
    {
        key: 'settings_listings',
        label: 'Listings: change compliance settings',
        description: 'Change the Listings compliance settings (expiry warning, takedown window, reminder email) on Settings → App settings.',
        category: 'listings',
        module: 'listings',
        kind: 'action',
        defaultTier: 'admin+',
        path: '/settings/general',
    },
    // Property management (Listings Phase 5, PM-1) — a separate insight section, not part of
    // Listings. The hand-written /api/property-management API checks these keys;
    // components/property-management/usePropertyManagementAccess mirrors it in the browser.
    {
        key: 'property_management',
        label: 'Property management: see the module',
        description: 'Open Property management: the managed properties you are the Property Manager of, and the lease intelligence preview.',
        category: 'property_management',
        module: 'property_management',
        kind: 'view',
        defaultTier: 'manager+',
        path: '/property-management',
    },
    {
        // The visibility switch (Noah 2026-10-02): with it, every managed property; without it, a
        // property_management holder sees only the properties it is the current Property Manager of.
        key: 'property_management_all',
        label: 'Property management: see every managed property',
        description: 'See every managed property, not only the ones you are the Property Manager of.',
        category: 'property_management',
        module: 'property_management',
        kind: 'action',
        defaultTier: 'manager+',
    },
    {
        // Every Property management write checks it (data/property-management-access.ts can_work),
        // not only assigning managers: the label says so, so a Property Manager is not left view-only.
        key: 'property_management_work',
        label: 'Property management: file and review leases',
        description: 'File lease documents, run and review the AI abstract, enter and confirm terms, mark leases managed, and assign or end a Property Manager.',
        category: 'property_management',
        module: 'property_management',
        kind: 'action',
        defaultTier: 'manager+',
    },
    // Administration
    {
        key: 'manage_users',
        label: 'Users: see Manage Users',
        description: 'Open Manage Users: invite users and manage their accounts.',
        category: 'admin',
        module: 'users',
        kind: 'view',
        defaultTier: 'manager+',
        path: '/users',
    },
    {
        key: 'settings',
        label: 'Settings: see the settings page',
        description: 'Open the Settings page, with Admin info, Calculated fields and Widget Permissions. Every other settings page has its own switch.',
        category: 'admin',
        module: 'settings',
        kind: 'view',
        defaultTier: 'manager+',
        path: '/settings',
    },
    {
        key: 'settings_permissions',
        label: 'Settings: security & permissions',
        description: 'Edit roles, feature access and which role each user has, on Security & Permissions.',
        category: 'admin',
        module: 'settings',
        kind: 'action',
        defaultTier: 'admin+',
        path: '/settings/permissions',
    },
    // `settings_suggestions` (standalone field-change suggestions page)
    // was retired 2026-06-21 as part of the Plan A consolidation. Review
    // surface lives at /reviews/submissions, gated by the `reviews` key.
    {
        key: 'settings_submarkets',
        label: 'Settings: submarket boundaries',
        description: 'Upload and manage the submarket GeoJSON boundaries.',
        category: 'admin',
        module: 'settings',
        kind: 'action',
        defaultTier: 'manager+',
        path: '/settings/submarkets',
    },
    // AI model settings (LAB-A, docs/ai/ai-lab-design.md §1 / §5): which AI model each import task uses.
    // Server: /api/ai-lab (feature:ai_lab reads, feature:ai_lab_approve saves / resets); migration
    // 20261006185949_ai_model_settings grants both to Super Admin and Admin. Never the email whitelist.
    {
        key: 'ai_lab',
        label: 'Settings: see AI model settings',
        description: 'See which AI model, reasoning effort and timeout each import task uses, where it comes from, and its history. AIG staff only for now.',
        category: 'admin',
        module: 'settings',
        kind: 'action',
        defaultTier: 'admin+',
        path: '/settings/ai',
    },
    {
        key: 'ai_lab_approve',
        label: 'Settings: change AI model settings',
        description: 'Change or reset an import task\'s AI model, reasoning effort and timeout. A reason is required and every change is audited.',
        category: 'admin',
        module: 'settings',
        kind: 'action',
        defaultTier: 'admin+',
    },
    // M63: a user's hire date, home address and birth year (server: /api/users/:id/private).
    // Granted to no role by default, and NOT implied by Admin / Super Admin (see
    // EXACT_GRANT_ONLY_FEATURES below). Migration 20261008090000 seeds the permission row.
    {
        key: 'hr_protected_fields',
        label: 'Users: protected HR fields',
        description: 'See and edit a user\'s hire date, home address and birth year. Admin and Super Admin do not get it automatically: grant it to the role that needs it.',
        category: 'admin',
        module: 'users',
        kind: 'action',
        defaultTier: 'none',
    },
    // M64: granted to the HR Director / HR Assistant roles by migration 20261008200000; not implied by Admin.
    {
        key: 'hr_directory',
        label: 'Directory: see the people directory',
        description: 'Open the people directory. Granted to HR Director and HR Assistant; Admin does not get it automatically.',
        category: 'admin',
        module: 'directory',
        kind: 'view',
        defaultTier: 'none',
    },
    // 'hr_requests' (M64, for the M79 HR requests inbox) was removed 2026-10-28: nothing reads it yet.
    // Migration 20261008200000's grants stay in place; M79 adds the key back with its inbox.
    {
        key: 'settings_data_maintenance',
        label: 'Settings: run data maintenance',
        description: 'Run one-off reconciliation jobs, such as filling in counties from coordinates.',
        category: 'admin',
        module: 'settings',
        kind: 'action',
        defaultTier: 'admin+',
        path: '/settings/data-maintenance',
    },
    {
        key: 'settings_data_sources',
        label: 'Settings: manage data sources',
        description: 'Add and edit the data sources (name, code, type, trust rank) that ingestion and provenance use.',
        category: 'admin',
        module: 'settings',
        kind: 'action',
        defaultTier: 'admin+',
        path: '/settings/sources',
    },
    {
        key: 'settings_property_types_uses',
        label: 'Settings: property types and lease lookups',
        description: 'Curate the Property Types, their Uses, Lease Types and Additional Lease Types.',
        category: 'admin',
        module: 'settings',
        kind: 'action',
        defaultTier: 'manager+',
        path: '/settings/data-curation?tab=types',
    },
    {
        key: 'settings_reference_data',
        label: 'Settings: reference data',
        description: 'Curate the lookup lists (property statuses, building classes, parking types, construction purposes, property rights, transaction and comp lookups) and the fields each property type shows.',
        category: 'admin',
        module: 'settings',
        kind: 'action',
        defaultTier: 'manager+',
        path: '/settings/data-curation',
    },
    // D2-R3 (decision 35A) + CF-15 (decision 26): the edit key of the platform settings groups (@aiga/app-config/settings):
    // data.delete_safety, ai.lab, ai.corrections, comps.promotion, data.quality, data.freshness. Production already holds the permission row, granted to
    // Super Admin and Admin (an orphan until now, config-plan §10 F3); admins pass anyway.
    {
        key: 'system_settings',
        label: 'Settings: change system settings',
        description: 'Change the platform settings on Settings → App settings: delete safety, AI lab caps and retention, AI corrections, deal-to-comp promotion, data quality limits and freshness bands. Every change needs a reason.',
        category: 'admin',
        module: 'settings',
        kind: 'action',
        defaultTier: 'admin+',
        path: '/settings/general',
    },
];
export const FEATURE_CATALOG = CATALOG;
/** The `perm_name` of a catalog feature (`featurePerm('listings')` → 'feature:listings'). */
export function featurePerm(key) {
    return `${FEATURE_PERM_PREFIX}${key}`;
}
/**
 * Keys with NO admin shortcut and NO tier fallback: true only when the user holds the exact
 * permission. The web's useFeatureAccess reads this set; the server enforces the same rule
 * with its own holds-key check (a Super Admin without the key gets 403).
 * Exception: AIG staff (isAigStaff in ../security: a whitelisted email AND an admin role) hold
 * every key, these included (docs/SECURITY.md "AIG staff hold every key"). A client Super Admin
 * does not.
 */
export const EXACT_GRANT_ONLY_FEATURES = new Set(['hr_protected_fields']);
//# sourceMappingURL=feature-catalog.js.map